import React, { Component } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';

class One3D extends Component {
  mount = React.createRef();

  componentDidMount() {
    const mount = this.mount.current;
    if (!mount) return;

    this.scene = new THREE.Scene();
    this.clock = new THREE.Clock();
    this.camera = new THREE.PerspectiveCamera(85, 1, 0.01, 10);
    this.camera.position.z = 3;
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    mount.appendChild(this.renderer.domElement);

    this.pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    this.environment = this.pmremGenerator.fromScene(new RoomEnvironment(), 0.04);
    this.scene.environment = this.environment.texture;
    this.resize();
    window.addEventListener('resize', this.resize);

    this.loader = new GLTFLoader();
    this.loader.load('/cases/img/one/3d/Circle2.gltf', (gltf) => {
      if (!this.scene) return;
      this.model = gltf.scene;
      const bounds = new THREE.Box3().setFromObject(this.model);
      const center = bounds.getCenter(new THREE.Vector3());
      this.model.position.sub(center);
      this.model.scale.setScalar(window.innerWidth <= 1000 ? 1.08 : 1);
      this.scene.add(this.model);
      if (!this.props.autoRotate) this.startMotion();
    });
    this.renderer.setAnimationLoop(this.renderScene);
  }

  startMotion = () => {
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!window.gsap || !window.ScrollTrigger || reduceMotion) return;
    window.gsap.registerPlugin(window.ScrollTrigger);
    this.rotationTween = window.gsap.to(this.model.rotation, {
      y: 6,
      ease: 'none',
      scrollTrigger: {
        trigger: this.mount.current,
        start: 'top bottom',
        end: () => '+=' + window.innerHeight * 4,
        scrub: 1,
      },
    });
  };

  resize = () => {
    if (!this.mount.current || !this.renderer || !this.camera) return;
    const width = this.mount.current.clientWidth || 1;
    const height = this.mount.current.clientHeight || 1;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.position.z = 3;
    if (this.model) this.model.scale.setScalar(window.innerWidth <= 1000 ? 1.08 : 1);
    this.camera.updateProjectionMatrix();
  };

  renderScene = () => {
    if (this.model && this.props.autoRotate) {
      const delta = Math.min(this.clock.getDelta(), 0.05);
      this.model.rotation.y += delta * 0.42;
    }
    if (this.renderer && this.scene && this.camera) this.renderer.render(this.scene, this.camera);
  };

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize);
    if (this.rotationTween) {
      if (this.rotationTween.scrollTrigger) this.rotationTween.scrollTrigger.kill();
      this.rotationTween.kill();
    }
    if (this.renderer) {
      this.renderer.setAnimationLoop(null);
      this.renderer.dispose();
      if (this.renderer.domElement.parentNode) this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
    }
    if (this.environment) this.environment.dispose();
    if (this.pmremGenerator) this.pmremGenerator.dispose();
    this.scene = null;
    this.camera = null;
    this.renderer = null;
  }

  render() {
    const className = this.props.className || 'one-object-scene__three';
    return <div className={className} ref={this.mount} aria-hidden="true" />;
  }
}

export default One3D;
